"use client";

import React, { useState } from 'react';
import ProductForm from '@/components/ProductForm';
import { Card, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface MultiStepProductFormProps {
  session: any;
  profile?: any;
  initialProduct?: any;
  onSubmit: any;
  loading: boolean;
  submitButtonText: string;
  initialProductType?: string | null;
  initialBarcode?: string;
}

const MultiStepProductForm: React.FC<MultiStepProductFormProps> = ({ session, profile, initialProduct, onSubmit, loading, submitButtonText, initialProductType, initialBarcode }) => {
  // For 'groceries' (almacén) we show a single unified view (UI-only change)
  const isGroceries = initialProductType === 'groceries';

  if (isGroceries) {
    return (
      <Card className="rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="p-4 border-b border-primary/30 bg-card">
          <div className="text-sm font-medium">Almacén</div>
        </div>
        <ProductForm
          session={session}
          profile={profile}
          initialProduct={initialProduct}
          onSubmit={onSubmit}
          loading={loading}
          submitButtonText={submitButtonText}
          hideTitleInput={false}
          hideTypeSelector={false}
          initialProductType={initialProductType}
          activeStep={undefined}
          initialBarcode={initialBarcode}
        />
      </Card>
    );
  }

  // Non-groceries: keep existing multi-step behaviour
  const typesWithoutDetails = new Set(['book', 'art', 'clothing', 'groceries']);
  const showDetails = !typesWithoutDetails.has(initialProductType || '');
  const visibleSteps = showDetails
    ? [
        { id: 1, label: 'Información', target: 1 },
        { id: 2, label: 'Media', target: 2 },
        { id: 3, label: 'Detalles', target: 3 },
        { id: 4, label: 'Precio', target: 4 },
      ]
    : [
        { id: 1, label: 'Información', target: 1 },
        { id: 2, label: 'Media', target: 2 },
        { id: 3, label: 'Precio', target: 4 },
      ];

  const [index, setIndex] = useState<number>(0); // index into visibleSteps
  const maxIndex = visibleSteps.length - 1;

  const next = () => setIndex((i) => Math.min(maxIndex, i + 1));
  const prev = () => setIndex((i) => Math.max(0, i - 1));

  return (
    <Card className="rounded-lg border bg-card text-card-foreground shadow-sm">
      {/* Stepper visual */}
      <div className="p-4 border-b border-primary/30 bg-card">
        <ol className="flex items-center justify-between gap-4 max-w-full mx-auto">
          {visibleSteps.map((s, i) => {
            const active = i === index;
            const done = i < index;
            return (
              <li key={s.id} className="flex-1">
                <button
                  type="button"
                  onClick={() => setIndex(i)}
                  className="w-full text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium ring-offset-background ${active ? 'bg-primary text-white' : done ? 'bg-primary/20 text-primary' : 'border bg-transparent text-muted-foreground'}`}>
                      {i + 1}
                    </div>
                    <div className={`text-sm ${active ? 'font-semibold text-foreground' : 'sr-only'}`}>{s.label}</div>
                  </div>
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      <ProductForm
        session={session}
        profile={profile}
        initialProduct={initialProduct}
        onSubmit={onSubmit}
        loading={loading}
        submitButtonText={submitButtonText}
        hideTitleInput={false}
        hideTypeSelector={false}
        initialProductType={initialProductType}
        activeStep={visibleSteps[index].target}
        initialBarcode={initialBarcode}
      />

      <CardFooter className="flex items-center justify-between">
        <div className="text-sm">Paso {index + 1} de {visibleSteps.length}</div>
        <div className="flex items-center gap-2">
          {index > 0 && (
            <Button variant="ghost" onClick={prev} disabled={loading}>Anterior</Button>
          )}
          {index < maxIndex && (
            <Button onClick={next} disabled={loading}>Siguiente</Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

export default MultiStepProductForm;
